/* LDW instant quote widget. Mounts on every [data-ldw-quote] element.
   Flow: pick file -> POST /api/quotes -> PUT signed upload -> poll status -> pick options ->
   POST /api/quotes/{id}/cart (the backend prices it and makes a hidden product) -> /cart/add.js.
   Prices shown here are for display; the backend recomputes them for the cart. */
(function () {
  "use strict";

  var EXTENSIONS = [".stl", ".3mf", ".obj", ".step", ".stp"];
  var STEP = [".step", ".stp"];  // browsers can't draw these; the worker sends a preview mesh
  var POLL_MS = 2500;
  var POLL_LIMIT_MS = 6 * 60 * 1000;
  // three.js 0.186.1 is served from the theme's own assets (tools/vendor_three.py), next to this file
  var ASSETS = (document.currentScript && document.currentScript.src || "").replace(/[^/]*$/, "");
  var THREE_FILES = {
    "": "ldw-three.module.js",
    "/examples/jsm/controls/OrbitControls.js": "ldw-three-OrbitControls.js",
    "/examples/jsm/loaders/STLLoader.js": "ldw-three-STLLoader.js",
    "/examples/jsm/loaders/3MFLoader.js": "ldw-three-3MFLoader.js",
    "/examples/jsm/loaders/OBJLoader.js": "ldw-three-OBJLoader.js",
  };
  var PLATE_MM = 256;
  // shown when the section's "Print terms" setting is empty (same text as the setting's default)
  var DEFAULT_TERMS = [
    "We are a print service: we print your file as supplied and don't check, fix or change your design.",
    "Printed dimensions and fit are not guaranteed. 3D printing has normal tolerances, and we don't compensate for thermal shrinkage of the material.",
    "We won't print parts that are illegal to make or own.",
    "We reserve the right to refuse or cancel any print. If we cancel a paid order, you get a full refund.",
  ];

  function el(tag, attrs) {
    var node = document.createElement(tag);
    Object.keys(attrs || {}).forEach(function (k) {
      if (k === "text") node.textContent = attrs[k];
      else if (k === "class") node.className = attrs[k];
      else node.setAttribute(k, attrs[k]);
    });
    for (var i = 2; i < arguments.length; i++) if (arguments[i]) node.appendChild(arguments[i]);
    return node;
  }

  function money(n) {
    return "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function round2(n) {
    return Math.round(n * 100) / 100;
  }

  // Same rules and operation order as price_for() in worker/pricing.py (integer cents, round
  // half up), so the page and checkout can never differ by a cent: printing time + handling per
  // plate + material + support fee, a bulk discount for small parts, then the order minimum.
  function halfUp(x) { return Math.floor(x + 0.5); }

  // same tiers as discount_breaks() in worker/pricing.py: the biggest parts (one per plate),
  // small parts (many per plate), and everything in between
  function discountBreaks(perPlate, opt) {
    if (perPlate <= (opt.single_part_max_per_plate || 0) && opt.single_part_breaks && opt.single_part_breaks.length) {
      return opt.single_part_breaks;
    }
    return perPlate >= opt.discount_min_per_plate ? opt.quantity_breaks : (opt.large_part_breaks || []);
  }

  function discountRate(qty, perPlate, opt) {
    var rate = 0;
    discountBreaks(perPlate, opt).forEach(function (b) { if (qty >= b[0]) rate = b[1]; });
    return rate;
  }

  // cents before the order minimum: [full price, after the quantity discount, plates, rate]
  function discounted(opt, qty) {
    var cap = Math.max(1, opt.plate_capacity);
    var plates = Math.ceil(qty / cap);
    var hours = plates * opt.warmup_hours + qty * opt.print_hours;
    var printing = halfUp(hours * opt.machine_rate * 100);
    var handling = plates * halfUp(opt.handling_per_plate * 100);
    var full = printing + handling + qty * opt.material_cents + qty * opt.support_cents;
    var rate = discountRate(qty, cap, opt);
    return [full, full - Math.floor((full * halfUp(rate * 10000) + 5000) / 10000), plates, rate];
  }

  function priceFor(opt, qty) {
    var d = discounted(opt, qty), full = d[0], subtotal = d[1], rate = d[3];
    // never more per part than a smaller order (a new, partly filled plate adds setup cost):
    // walking up from 1, hold each quantity to the best per-part price so far, as the worker does
    var bestCents = discounted(opt, 1)[1], bestQty = 1;
    for (var n = 2; n <= qty; n++) {
      var here = Math.min(n === qty ? subtotal : discounted(opt, n)[1], Math.floor(n * bestCents / bestQty));
      if (here * bestQty < bestCents * n) { bestCents = here; bestQty = n; }
      if (n === qty) subtotal = here;
    }
    var saving = full - subtotal;
    var filler = Math.max(0, halfUp(opt.min_order * 100) - subtotal);
    return { rate: saving ? rate : 0, plates: d[2], cap: Math.max(1, opt.plate_capacity), full: full / 100, saving: saving / 100,
             subtotal: subtotal / 100, filler: filler / 100, total: (subtotal + filler) / 100 };
  }

  // ---- 3D preview: drawn in the customer's browser from their own file; never sent anywhere ----

  // Shopify's asset pipeline rewrites import() in theme scripts into require(), which browsers
  // don't have, so the modules are loaded through an injected <script type="module">.
  var libs = {};
  function load(path) {
    if (!libs[path]) {
      libs[path] = new Promise(function (resolve, reject) {
        var key = "__ldwq" + Math.random().toString(36).slice(2);
        window[key] = function (mod, err) {
          delete window[key];
          if (err) reject(err); else resolve(mod);
        };
        var s = document.createElement("script");
        s.type = "module";
        s.textContent = "import(" + JSON.stringify(ASSETS + THREE_FILES[path]) + ").then(function (m) { window." + key +
          "(m); }, function (e) { window." + key + "(null, e); });";
        document.head.appendChild(s);
      });
    }
    return libs[path];
  }

  function Viewer(wrap) {
    this.wrap = wrap;
    this.token = 0;
  }

  Viewer.prototype.clear = function () {
    this.token++;
    if (this.stop) this.stop();
    this.stop = null;
    this.applyOrientation = null;
    this.pendingOrientation = null;
    this.addSupports = null;
    this.pendingSupports = null;
    this.caption = null;
    this.hint = null;
    this.snap = null;
    this.paint = null;
    // the viewer area is always there, so the window never changes size
    this.wrap.replaceChildren(el("p", { class: "ldwq-viewer__msg ldwq-viewer__empty", text: "Your 3D model will appear here" }));
  };

  Viewer.prototype.show = function (file) {
    this.clear();
    var ext = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
    this.wrap.hidden = false;
    if (STEP.indexOf(ext) !== -1) {
      this.wrap.appendChild(el("p", { class: "ldwq-viewer__msg", text: "Your 3D preview will appear once the STEP file is converted…" }));
      return;
    }
    this.load(ext, ext === ".obj" ? file.text() : file.arrayBuffer());
  };

  // The worker's preview mesh (binary STL) for a STEP upload. Keeps any orientation/supports
  // that arrived first.
  Viewer.prototype.showRemote = function (url) {
    if (this.stop) this.stop();  // replacing an earlier preview: release its renderer first
    this.stop = null;
    this.applyOrientation = null;
    this.addSupports = null;
    this.load(".stl", fetch(url).then(function (r) {
      if (!r.ok) throw new Error("HTTP " + r.status);
      return r.arrayBuffer();
    }));
  };

  Viewer.prototype.load = function (ext, dataPromise) {
    var self = this;
    var token = this.token;
    var loader = { ".stl": "/examples/jsm/loaders/STLLoader.js", ".3mf": "/examples/jsm/loaders/3MFLoader.js",
                   ".obj": "/examples/jsm/loaders/OBJLoader.js" }[ext];
    this.wrap.replaceChildren(el("p", { class: "ldwq-viewer__msg", text: "Loading 3D preview…" }));
    Promise.all([load(""), load("/examples/jsm/controls/OrbitControls.js"), load(loader), dataPromise])
      .then(function (r) {
        if (token !== self.token) return;
        self.draw(r[0], r[1].OrbitControls, r[2], ext, r[3]);
      })
      .catch(function (e) {
        if (token !== self.token) return;
        console.warn("3D preview unavailable", e);
        self.clear();
      });
  };

  // Turns the model into the print orientation the worker chose. `r` is the rotation for row
  // vectors in the file's Z-up coordinates (p' = p @ r); may arrive before the model is drawn.
  // Caption under the model, e.g. "Shown as it will print"; may arrive before the model is drawn.
  Viewer.prototype.setCaption = function (text) {
    this.caption = text;
    if (this.hint) this.hint.textContent = text;
  };

  // The customer's color choice on the model ("" = the theme color), and a square JPEG of the
  // current view for the cart picture (null if there's no model on screen).
  Viewer.prototype.setColor = function (hex) {
    this.color = hex;
    if (this.paint) this.paint(hex);
  };

  Viewer.prototype.picture = function () {
    try {
      return this.snap ? this.snap() : null;
    } catch (e) {
      console.warn("cart picture unavailable", e);
      return null;
    }
  };

  Viewer.prototype.setOrientation = function (r) {
    if (!r) return;
    if (this.applyOrientation) this.applyOrientation(r);
    else this.pendingOrientation = r;
  };

  // Support toolpaths from the slicer (int16 segments in 0.1 mm, Z up, x/y centered on the
  // part's footprint; see worker/supports.py). Drawn under the part once both are loaded.
  Viewer.prototype.setSupports = function (url) {
    var self = this;
    var token = this.token;
    fetch(url)
      .then(function (r) { if (!r.ok) throw new Error("HTTP " + r.status); return r.arrayBuffer(); })
      .then(function (buf) {
        if (token !== self.token) return;
        var raw = new Int16Array(buf);
        var pos = new Float32Array(raw.length);
        for (var i = 0; i < raw.length; i += 3) {  // Z-up (x, y, z) -> three.js Y-up (x, z, -y)
          pos[i] = raw[i] / 10;
          pos[i + 1] = raw[i + 2] / 10;
          pos[i + 2] = -raw[i + 1] / 10;
        }
        if (self.addSupports) self.addSupports(pos);
        else self.pendingSupports = pos;
      })
      .catch(function (e) { console.warn("support preview unavailable", e); });
  };

  Viewer.prototype.draw = function (T, OrbitControls, mod, ext, data) {
    var self = this;
    var wrap = this.wrap;
    var rgb = getComputedStyle(wrap).getPropertyValue("--color-button").trim();
    // Double-sided, with normals recomputed below: many exported files have zero normals or
    // inconsistent winding, which would otherwise render dark or with missing faces.
    var themeColor = rgb ? "rgb(" + rgb + ")" : "#cccccc";
    var material = new T.MeshStandardMaterial({ color: themeColor, roughness: 0.55,
      metalness: 0.05, side: T.DoubleSide });
    this.paint = function (hex) { material.color.set(hex || themeColor); };
    if (this.color) this.paint(this.color);
    var object;
    if (ext === ".stl") object = new T.Mesh(new mod.STLLoader().parse(data), material);
    else if (ext === ".obj") object = new mod.OBJLoader().parse(data);
    else object = new mod.ThreeMFLoader().parse(data);
    object.traverse(function (o) {
      if (!o.isMesh) return;
      o.material = material;
      o.geometry.deleteAttribute("normal");
      o.geometry.computeVertexNormals();
    });

    // object -> print orientation (file coords) -> Z-up to three.js Y-up -> centered on the plate
    var oriented = new T.Group();
    oriented.matrixAutoUpdate = false;
    oriented.add(object);
    var yUp = new T.Group();
    yUp.rotation.x = -Math.PI / 2;
    yUp.add(oriented);
    var model = new T.Group();
    model.add(yUp);
    function layout() {
      model.position.set(0, 0, 0);
      model.updateMatrixWorld(true);
      var box = new T.Box3().setFromObject(model);
      if (box.isEmpty()) throw new Error("empty model");
      var center = box.getCenter(new T.Vector3());
      model.position.set(-center.x, -box.min.y, -center.z);
      model.updateMatrixWorld(true);
      return box.setFromObject(model).getBoundingSphere(new T.Sphere());
    }
    var sphere = layout();

    var scene = new T.Scene();
    scene.add(model);
    var plate = new T.Mesh(new T.BoxGeometry(PLATE_MM, 1, PLATE_MM),
      new T.MeshStandardMaterial({ color: 0x3a3a3e, roughness: 0.9 }));
    plate.position.y = -0.5;
    scene.add(plate);
    var grid = new T.GridHelper(PLATE_MM, 16, 0x6a6a70, 0x55555a);
    grid.position.y = 0.02;
    scene.add(grid);
    scene.add(new T.HemisphereLight(0xffffff, 0x404048, 2.2));

    var camera = new T.PerspectiveCamera(35, 1, 0.5, 5000);
    var key = new T.DirectionalLight(0xffffff, 2.2);
    key.position.set(1, 2, 3);
    camera.add(key);
    scene.add(camera);
    var view = new T.Vector3(1, 0.75, 1.25).normalize();

    var renderer = new T.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    var canvas = renderer.domElement;
    canvas.setAttribute("role", "img");
    canvas.setAttribute("aria-label", "3D preview of your model. Drag to rotate, scroll or pinch to zoom.");
    var controls = new OrbitControls(camera, canvas);
    controls.enableDamping = true;
    controls.enablePan = false;
    controls.maxPolarAngle = Math.PI * 0.495;  // stay above the plate
    function frame(s) {
      var dist = s.radius / Math.sin(T.MathUtils.degToRad(35 / 2)) * 1.15;
      controls.target.copy(s.center);
      camera.position.copy(s.center).add(view.clone().multiplyScalar(dist));
      controls.minDistance = s.radius * 1.2;
      controls.maxDistance = Math.max(dist * 4, PLATE_MM * 2);
    }
    frame(sphere);
    controls.autoRotate = !matchMedia("(prefers-reduced-motion: reduce)").matches;
    controls.autoRotateSpeed = 1.5;
    controls.addEventListener("start", function () { controls.autoRotate = false; });
    controls.update();

    var hint = el("p", { class: "ldwq-viewer__hint", text: this.caption || "Drag to rotate · Scroll to zoom" });
    this.hint = hint;
    wrap.replaceChildren(canvas, hint);
    this.applyOrientation = function (r) {
      oriented.matrix.set(r[0][0], r[1][0], r[2][0], 0, r[0][1], r[1][1], r[2][1], 0,
                          r[0][2], r[1][2], r[2][2], 0, 0, 0, 0, 1);
      view.copy(camera.position).sub(controls.target).normalize();  // keep the customer's viewing angle
      frame(layout());
      self.setCaption("Shown as it will print · Drag to rotate · Scroll to zoom");
    };
    if (this.pendingOrientation) this.applyOrientation(this.pendingOrientation);
    var supportMaterial = new T.LineBasicMaterial({ color: 0xd8d8de, transparent: true, opacity: 0.6 });
    this.addSupports = function (pos) {
      var geometry = new T.BufferGeometry();
      geometry.setAttribute("position", new T.BufferAttribute(pos, 3));
      scene.add(new T.LineSegments(geometry, supportMaterial));
      wrap.appendChild(el("p", { class: "ldwq-viewer__legend", text: "Gray lines: support material (removed after printing)" }));
    };
    if (this.pendingSupports) this.addSupports(this.pendingSupports);
    function resize() {
      var w = wrap.clientWidth, h = wrap.clientHeight;
      if (!w || !h) return;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    }
    var observer = new ResizeObserver(resize);
    observer.observe(wrap);
    resize();
    this.snap = function () {
      renderer.render(scene, camera);  // fresh frame: the canvas isn't kept between frames
      var src = renderer.domElement, side = Math.min(src.width, src.height), size = 600;
      var c = el("canvas", { width: String(size), height: String(size) });
      var g = c.getContext("2d");
      g.fillStyle = "#f2f2f4";
      g.fillRect(0, 0, size, size);
      g.drawImage(src, (src.width - side) / 2, (src.height - side) / 2, side, side, 0, 0, size, size);
      return c.toDataURL("image/jpeg", 0.85);
    };
    var raf;
    (function tick() {
      raf = requestAnimationFrame(tick);
      controls.update();
      renderer.render(scene, camera);
    })();

    this.stop = function () {
      cancelAnimationFrame(raf);
      observer.disconnect();
      controls.dispose();
      scene.traverse(function (o) {
        if (o.geometry) o.geometry.dispose();
        if (o.material) o.material.dispose();
      });
      renderer.dispose();
    };
  };

  // ---- quote flow ----

  function Widget(root) {
    this.root = root;
    this.api = (root.dataset.api || "").replace(/\/+$/, "");
    this.maxMb = Number(root.dataset.maxMb || 50);
    this.leadTime = root.dataset.leadTime || "";
    this.contactUrl = root.dataset.contactUrl || "";
    this.siteKey = root.dataset.turnstile || "";  // Cloudflare Turnstile bot check; off when empty
    // print terms the customer agrees to before ordering, one per line in the section setting
    this.terms = (root.dataset.terms || "").split(/\r?\n/).map(function (t) { return t.trim(); }).filter(Boolean);
    if (!this.terms.length) this.terms = DEFAULT_TERMS;
    // A self-contained "window": title bar with the steps, the 3D view, and a fixed-height side
    // panel that scrolls on its own, so nothing on the page moves as the quote progresses.
    var viewerWrap = el("div", { class: "ldwq-viewer" });
    this.viewer = new Viewer(viewerWrap);
    this.steps = ["Upload", "Slicing", "Quote"].map(function (label, i) {
      return el("li", { class: "ldwq-step-item" }, el("span", { class: "ldwq-step-num", text: String(i + 1) }), el("span", { text: label }));
    });
    var stepList = el("ol", { class: "ldwq-steps", "aria-label": "Progress" });
    this.steps.forEach(function (li) { stepList.appendChild(li); });
    this.panel = el("div", { class: "ldwq-side" });
    root.replaceChildren(el("div", { class: "ldwq-window" },
      el("div", { class: "ldwq-titlebar" }, el("span", { class: "ldwq-titlebar__name", text: "Instant quote" }), stepList),
      el("div", { class: "ldwq-body" }, viewerWrap, this.panel)));
    // only shows anything if Turnstile wants the visitor to click a checkbox
    this.botBox = el("div", { class: "ldwq-turnstile" });
    root.appendChild(this.botBox);
    this.showUpload();
  }

  var turnstileReady;
  function loadTurnstile() {
    if (!turnstileReady) {
      turnstileReady = new Promise(function (resolve, reject) {
        window.__ldwqTurnstile = function () { resolve(window.turnstile); };
        var s = document.createElement("script");
        s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=__ldwqTurnstile";
        s.async = true;
        s.onerror = function () { turnstileReady = null; reject(new Error("bot check failed to load")); };
        document.head.appendChild(s);
      });
    }
    return turnstileReady;
  }

  // A fresh single-use Turnstile token for the next quote ("" when the check is off).
  Widget.prototype.botToken = function () {
    var self = this;
    if (!this.siteKey) return Promise.resolve("");
    return loadTurnstile().then(function (ts) {
      return new Promise(function (resolve, reject) {
        var fail = function () { reject(new Error("The security check didn't complete. Please refresh the page and try again.")); };
        if (self.botId != null) ts.remove(self.botId);
        self.botId = ts.render(self.botBox, { sitekey: self.siteKey, action: "quote", appearance: "interaction-only",
          callback: resolve, "error-callback": fail, "expired-callback": fail, "timeout-callback": fail });
      });
    });
  };

  Widget.prototype.setStep = function (n) {
    this.steps.forEach(function (li, i) {
      li.classList.toggle("is-done", i + 1 < n);
      li.classList.toggle("is-current", i + 1 === n);
      if (i + 1 === n) li.setAttribute("aria-current", "step"); else li.removeAttribute("aria-current");
    });
  };

  Widget.prototype.render = function (node) {
    this.panel.replaceChildren(node);
    this.panel.scrollTop = 0;
  };

  Widget.prototype.fail = function (message) {
    var self = this;
    var again = el("button", { type: "button", class: "button button--secondary", text: "Try another file" });
    again.addEventListener("click", function () { self.showUpload(); });
    this.render(el("div", { class: "ldwq-panel is-centered", role: "alert" },
      el("p", { class: "ldwq-error", text: message }), again));
  };

  Widget.prototype.showUpload = function () {
    var self = this;
    this.activeJob = null;     // stops checking on any earlier quote
    this.updateQuote = null;
    var input = el("input", { type: "file", class: "ldwq-file", id: "ldwq-file-" + Math.random().toString(36).slice(2) });
    // iOS greys out any file type it has no system type for (STL and 3MF among them), so iPhones
    // and iPads get no filter; start() checks the extension either way.
    var ios = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    if (!ios) input.setAttribute("accept", EXTENSIONS.join(","));
    var drop = el("label", { class: "ldwq-drop", for: input.id },
      el("span", { class: "ldwq-drop__title", text: "Drop your 3D model here or click to choose a file" }),
      el("span", { class: "ldwq-drop__hint", text: "STL, 3MF, OBJ or STEP, up to " + this.maxMb + " MB" }));
    this.viewer.clear();
    input.addEventListener("change", function () { if (input.files[0]) self.start(input.files[0]); });
    ["dragenter", "dragover"].forEach(function (t) {
      drop.addEventListener(t, function (e) { e.preventDefault(); drop.classList.add("is-over"); });
    });
    ["dragleave", "drop"].forEach(function (t) {
      drop.addEventListener(t, function (e) { e.preventDefault(); drop.classList.remove("is-over"); });
    });
    drop.addEventListener("drop", function (e) {
      if (e.dataTransfer.files[0]) self.start(e.dataTransfer.files[0]);
    });
    this.setStep(1);
    this.render(el("div", { class: "ldwq-panel is-centered" }, input, drop));
  };

  // One progress panel with a spinner, updated in place. fraction (0..1, or null when unknown) only
  // tracks the highest value shown so far, so the percentage never moves backwards.
  Widget.prototype.progress = function (title, detail, fraction) {
    var p = this.progressView;
    if (!p || p.panel.parentNode !== this.panel) {
      p = this.progressView = { status: el("p", { class: "ldwq-status" }), hint: el("p", { class: "ldwq-hint" }), shown: 0 };
      p.panel = el("div", { class: "ldwq-panel is-centered is-progress", "aria-live": "polite" },
        el("div", { class: "ldwq-progress-row" }, el("span", { class: "ldwq-spinner", "aria-hidden": "true" }), p.status),
        p.hint);
      this.render(p.panel);
    }
    p.status.textContent = title;
    p.hint.textContent = detail || "";
    if (fraction != null) p.shown = Math.max(p.shown, fraction);
  };

  Widget.prototype.start = function (file) {
    var self = this;
    var name = file.name.toLowerCase();
    if (!EXTENSIONS.some(function (x) { return name.endsWith(x); })) return this.fail("Please upload an STL, 3MF, OBJ or STEP file.");
    this.previewUrl = null;
    if (file.size === 0) return this.fail("That file is empty.");
    if (file.size > this.maxMb * 1024 * 1024) return this.fail("That file is larger than " + this.maxMb + " MB. Please contact us for a quote.");
    this.viewer.show(file);
    this.progress(this.siteKey ? "Getting ready…" : "Uploading " + file.name + "… 0%", null, 0);
    this.botToken()
      .then(function (token) {
        if (token) self.progress("Uploading " + file.name + "… 0%", null, 0);
        return fetch(self.api + "/api/quotes", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(token ? { filename: file.name, size: file.size, turnstile: token }
            : { filename: file.name, size: file.size }),
        });
      })
      .then(function (r) { return r.json().then(function (b) { if (!r.ok) throw new Error(b.error); return b; }); })
      .then(function (job) { return self.upload(file, job); })
      .catch(function (e) {
        // fetch throws TypeError ("Failed to fetch") when the service can't be reached at all
        self.fail(e instanceof TypeError ? "We couldn't reach the quote service. Please check your connection and try again."
          : (e && e.message) || "Something went wrong. Please try again.");
      });
  };

  Widget.prototype.upload = function (file, job) {
    var self = this;
    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      xhr.open("PUT", job.upload_url);
      xhr.upload.onprogress = function (e) {
        if (e.lengthComputable) self.progress("Uploading " + file.name + "… " + Math.round(e.loaded / e.total * 100) + "%", null, e.loaded / e.total);
      };
      xhr.onload = function () {
        if (xhr.status === 202) {
          self.progressView = null;  // the slicing percentage starts again from 0
          self.activeJob = job.id; self.poll(job, Date.now()); resolve(); return;
        }
        var msg;
        try { msg = JSON.parse(xhr.responseText).error; } catch (_) { msg = null; }
        reject(new Error(msg || "The upload failed. Please try again."));
      };
      xhr.onerror = function () { reject(new Error("The upload failed. Please check your connection and try again.")); };
      xhr.send(file);
    });
  };

  Widget.prototype.poll = function (job, started) {
    var self = this;
    fetch(job.status_url, { cache: "no-store" })
      .then(function (r) { return r.json(); })
      .then(function (s) {
        // the worker may send a STEP model first and then the laid-out plate: show the newest
        if (s.preview_url && s.preview_url !== self.previewUrl) {
          self.previewUrl = s.preview_url;
          self.viewer.showRemote(s.preview_url);
        }
        if (self.activeJob !== job.id) return;  // the customer started over
        // first price(s) ready: show them now and keep checking until every option is in
        if (s.options && s.options.length && (s.status === "processing" || s.status === "done")) {
          if (self.updateQuote) self.updateQuote(s); else self.showQuote(s);
          if (s.status === "processing") setTimeout(function () { self.poll(job, started); }, POLL_MS);
          return;
        }
        if (s.status === "done") return self.fail("We couldn't generate a quote for that file. Please contact us.");
        self.setStep(2);
        if (s.status === "failed") return self.fail(s.error);
        if (Date.now() - started > POLL_LIMIT_MS) {
          return self.fail("This is taking longer than usual. Please try again later or contact us with quote " + (job.reference || job.id) + ".");
        }
        if (s.status === "processing" && typeof s.progress === "number") {
          // the worker reports stages; between them the percentage creeps on (never past the next stage)
          // so a long slice still shows movement
          var shown = self.progressView ? self.progressView.shown : 0;
          var f = Math.max(0.03, s.progress, Math.min(shown + 0.015, s.progress + 0.15, 0.95));
          self.progress("Slicing your model… " + Math.round(f * 100) + "%",
            "Working out your price. Keep this page open.", f);
        } else if (s.status === "queued" && s.queue_position > 1) {
          self.progress("You're number " + s.queue_position + " in line…",
            "Another quote is being sliced first. Keep this page open.", null);
        } else {
          self.progress("Waiting for the slicer…", "This usually takes under a minute. Keep this page open.", null);
        }
        setTimeout(function () { self.poll(job, started); }, POLL_MS);
      })
      .catch(function (e) {
        console.warn("quote status check failed; retrying", e);
        setTimeout(function () { self.poll(job, started); }, POLL_MS * 2);
      });
  };

  // A row of toggle buttons; returns the fieldset and a function that marks the chosen value.
  function choiceGroup(label, items, current, onPick) {
    var group = el("div", { class: "ldwq-choices", role: "radiogroup", "aria-label": label });
    var buttons = items.map(function (it) {
      var b = el("button", { type: "button", class: "ldwq-choice", role: "radio", text: it.label });
      b.addEventListener("click", function () { onPick(it.value); });
      group.appendChild(b);
      return b;
    });
    function mark(value) {
      items.forEach(function (it, i) { buttons[i].setAttribute("aria-checked", String(it.value === value)); });
    }
    // flags the buttons whose option (combined with the other group's choice) isn't priced yet
    function flag(isPending) {
      items.forEach(function (it, i) { buttons[i].classList.toggle("is-pending", isPending(it.value)); });
    }
    mark(current);
    return { node: el("fieldset", { class: "ldwq-field" }, el("legend", { text: label }), group), mark: mark, flag: flag };
  }

  // Controls are built once and only the price area is redrawn, so focus and typed notes survive.
  Widget.prototype.showQuote = function (quote) {
    var self = this;
    var options = quote.options;
    this.setStep(3);
    try {
      this.viewer.setOrientation(quote.model.orientation);
    } catch (e) {
      console.warn("3D preview could not show the print orientation", e);  // never block the price
    }
    if (quote.supports_url) this.viewer.setSupports(quote.supports_url);
    if (quote.model.pieces > 1 && !quote.model.orientation) {  // laid out by the worker
      this.viewer.setCaption("Shown as it will print: " + quote.model.pieces + " pieces · Drag to rotate · Scroll to zoom");
    }
    // every choice comes from the worker up front; options fill in as their slices finish
    var materials = (quote.choices && quote.choices.materials) || [], qualities = (quote.choices && quote.choices.qualities) || [];
    if (!materials.length) {
      options.forEach(function (o) {
        if (!materials.some(function (m) { return m.value === o.material; })) materials.push({ value: o.material, label: o.material_label });
        if (!qualities.some(function (q) { return q.value === o.quality; })) qualities.push({ value: o.quality, label: o.quality_label });
      });
    }
    var pending = quote.pending || [], unavailable = quote.unavailable || [];
    var state = { material: options[0].material, quality: options[0].quality, qty: 1 };
    var maxQty = options[0].max_quantity;
    function current() {
      return options.filter(function (o) { return o.material === state.material && o.quality === state.quality; })[0];
    }
    function isPending(material, quality) { return pending.indexOf(quality + "|" + material) !== -1; }
    function flagPending() {
      materialGroup.flag(function (m) { return isPending(m, state.quality); });
      qualityGroup.flag(function (q) { return isPending(state.material, q); });
    }

    var materialGroup = choiceGroup("Material", materials, state.material, function (v) {
      state.material = v; materialGroup.mark(v); flagPending(); update(); showColors(true);
    });

    // color: no extra cost, listed per material as squares to pick; hidden for materials without colors
    state.color = quote.color && quote.color.material === state.material ? quote.color.color : "";
    var swatches = el("div", { class: "ldwq-swatches", role: "radiogroup", "aria-label": "Color" });
    var colorName = el("span", { class: "ldwq-color-name" });
    var colorStatus = el("span", { class: "ldwq-hint ldwq-notes-status", "aria-live": "polite" });
    var colorField = el("fieldset", { class: "ldwq-field" },
      el("legend", {}, document.createTextNode("Color "), colorName), swatches, colorStatus);
    function colorsFor(material) {
      var m = materials.filter(function (x) { return x.value === material; })[0];
      return (m && m.colors) || [];
    }
    function saveColor() {
      colorStatus.textContent = "";
      fetch(self.api + "/api/quotes/" + quote.id + "/color", {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ material: state.material, color: state.color }),
      })
        .then(function (r) { return r.json().then(function (b) { if (!r.ok) throw new Error(b.error); }); })
        .then(function () { colorStatus.textContent = "Saved with your quote."; })
        .catch(function (e) { colorStatus.textContent = (e && e.message) || "Couldn't save. Please try again."; });
    }
    function markColor() {
      Array.prototype.forEach.call(swatches.children, function (b) {
        var on = b.dataset.color === state.color;
        b.classList.toggle("is-selected", on);
        b.setAttribute("aria-checked", on ? "true" : "false");
      });
      colorName.textContent = "· " + (state.color || "No preference");
      var pick = colorsFor(state.material).filter(function (c) { return c.name === state.color; })[0];
      self.viewer.setColor(pick ? pick.hex : "");
      cartChanged();
    }
    // materialChanged: keep the color if the new material has it too, otherwise back to no preference
    function showColors(materialChanged) {
      var colors = colorsFor(state.material);
      colorField.hidden = !colors.length;
      if (!colors.some(function (c) { return c.name === state.color; })) state.color = "";
      var items = [{ name: "", label: "No preference" }].concat(colors.map(function (c) { return { name: c.name, label: c.name, hex: c.hex }; }));
      swatches.replaceChildren.apply(swatches, items.map(function (c) {
        var b = el("button", { type: "button", role: "radio", class: "ldwq-swatch" + (c.hex ? "" : " ldwq-swatch--any"),
          title: c.label, "aria-label": c.label });
        b.dataset.color = c.name;
        if (c.hex) b.style.backgroundColor = c.hex;
        b.addEventListener("click", function () { state.color = c.name; markColor(); saveColor(); });
        return b;
      }));
      markColor();
      if (materialChanged && colors.length) saveColor();
      else if (materialChanged) colorStatus.textContent = "";
    }
    var qualityGroup = choiceGroup("Quality", qualities, state.quality, function (v) {
      state.quality = v; qualityGroup.mark(v); flagPending(); update(); cartChanged();
    });

    // Ordering: the backend prices the chosen option and makes a hidden product for it, which goes
    // into the store's own cart. Without ordering set up, "Request this print" goes to the contact page.
    var addButton = el("button", { type: "button", class: "button", text: "Add to cart" });
    var cartStatus = el("p", { class: "ldwq-hint ldwq-cart-status", "aria-live": "polite" });
    function cartChanged() {
      addButton.textContent = "Add to cart";
      cartStatus.replaceChildren();
    }
    var agree = el("input", { type: "checkbox", id: "ldwq-agree" });
    var termsList = el("ul", { class: "ldwq-terms__list" });
    self.terms.forEach(function (t) { termsList.appendChild(el("li", { text: t })); });
    var termsField = el("fieldset", { class: "ldwq-field ldwq-terms" }, el("legend", { text: "Print terms" }), termsList,
      el("label", { class: "ldwq-agree", for: "ldwq-agree" }, agree, el("span", { text: "I have read and agree to these print terms." })));
    // Add to cart stays greyed out until the terms are ticked (and while an add is under way)
    var adding = false;
    function updateAddButton() {
      addButton.disabled = adding || !agree.checked;
      if (agree.checked) addButton.removeAttribute("title");
      else addButton.title = "Please agree to the print terms first";
    }
    agree.addEventListener("change", function () {
      cartStatus.replaceChildren();
      updateAddButton();
    });
    addButton.addEventListener("click", function () {
      var opt = current();
      if (!opt || addButton.disabled) return;
      adding = true;
      updateAddButton();
      addButton.textContent = "Adding…";
      cartStatus.replaceChildren();
      var summary = state.qty + " × " + opt.material_label + ", " + opt.quality_label + (state.color ? ", " + state.color : "");
      fetch(self.api + "/api/quotes/" + quote.id + "/cart", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ material: state.material, quality: state.quality, quantity: state.qty, color: state.color,
                               notes: notesBox.value, image: self.viewer.picture(), agreed: true, terms: self.terms.join("\n") }),
      })
        .then(function (r) { return r.json().then(function (b) { if (!r.ok) throw new Error(b.error); return b; }); })
        .then(function (line) { return addToShopCart(line); })
        .then(function () {
          addButton.textContent = "Add another";
          cartStatus.replaceChildren(document.createTextNode("Added to your cart: " + summary + ". "),
            el("a", { href: shopRoot() + "cart", text: "View cart" }));
        })
        .catch(function (e) {
          addButton.textContent = "Add to cart";
          cartStatus.textContent = e instanceof TypeError ? "We couldn't reach the store. Please check your connection and try again."
            : (e && e.message) || "Something went wrong. Please try again.";
        })
        .then(function () { adding = false; updateAddButton(); });
    });
    var progressLine = el("p", { class: "ldwq-hint ldwq-more", "aria-live": "polite" });
    // always present (never hidden) so nothing below it moves when the last option arrives
    function showProgress() {
      var total = materials.length * qualities.length;
      var ready = total - pending.length - unavailable.length;
      progressLine.textContent = pending.length
        ? "Calculating options… " + ready + " of " + total + " ready"
        : "All " + ready + " options ready";
    }
    // later polls bring more options; keep the customer's selections, quantity and notes
    this.updateQuote = function (next) {
      options = next.options;
      pending = next.pending || [];
      unavailable = next.unavailable || [];
      flagPending();
      showProgress();
      update();
    };

    var qtyInput = el("input", { type: "number", min: "1", max: String(maxQty), step: "1", value: "1",
      class: "field__input ldwq-qty", id: "ldwq-qty", inputmode: "numeric" });
    var minus = el("button", { type: "button", class: "ldwq-step", "aria-label": "Decrease quantity", text: "−" });
    var plus = el("button", { type: "button", class: "ldwq-step", "aria-label": "Increase quantity", text: "+" });
    function setQty(q) {
      state.qty = Math.min(maxQty, Math.max(1, isFinite(q) ? Math.floor(q) : 1));
      cartChanged();
      qtyInput.value = String(state.qty);
      minus.disabled = state.qty <= 1;
      plus.disabled = state.qty >= maxQty;
      update();
    }
    minus.addEventListener("click", function () { setQty(state.qty - 1); });
    plus.addEventListener("click", function () { setQty(state.qty + 1); });
    qtyInput.addEventListener("change", function () { setQty(Number(qtyInput.value)); });

    var priceArea = el("div", { class: "ldwq-price-area", "aria-live": "polite" });
    function update() {
      var opt = current();
      if (!opt) {
        var gone = unavailable.indexOf(state.quality + "|" + state.material) !== -1;
        priceArea.replaceChildren(el("p", { class: "ldwq-status ldwq-calculating", text: gone
          ? "This option isn't available for this model. Please choose another."
          : "Calculating this option… it will appear here in a moment." }));
        return;
      }
      var p = priceFor(opt, state.qty);
      var rows = el("dl", { class: "ldwq-price" },
        el("dt", { text: "Parts (" + state.qty + ")" }), el("dd", { text: money(p.full) }));
      // always listed, $0.00 when nothing applies, so the breakdown keeps the same shape
      rows.appendChild(el("dt", { class: p.saving > 0 ? "ldwq-saving" : "", text: "Qty discount" }));
      rows.appendChild(el("dd", { class: p.saving > 0 ? "ldwq-saving" : "", text: "−" + money(p.saving) }));
      if (p.filler > 0) {
        rows.appendChild(el("dt", { text: "Small order fee (" + money(opt.min_order) + " minimum)" }));
        rows.appendChild(el("dd", { text: money(p.filler) }));
      }
      rows.appendChild(el("dt", { class: "ldwq-total", text: "Total" }));
      rows.appendChild(el("dd", { class: "ldwq-total", text: money(p.total) }));
      rows.appendChild(el("dt", { class: "ldwq-each", text: "Works out to" }));
      rows.appendChild(el("dd", { class: "ldwq-each", text: money(p.total / state.qty) + " per part" }));

      var info = el("ul", { class: "ldwq-notes" });
      info.appendChild(el("li", { text: "Estimated filament: " + (opt.est_grams * state.qty).toFixed(1) + " g, including supports." }));
      var next = discountBreaks(p.cap, opt).filter(function (b) { return b[0] > state.qty && b[0] <= maxQty; })[0];
      if (next) info.appendChild(el("li", { text: "Order " + next[0] + " or more for " + Math.round(next[1] * 100) + "% off." }));
      if (self.leadTime) info.appendChild(el("li", { text: self.leadTime }));
      if (quote.valid_until) {
        info.appendChild(el("li", { text: "Price good until " + new Date(quote.valid_until * 1000)
          .toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) + "." }));
      }
      priceArea.replaceChildren(rows, info);
    }

    var notesBox = el("textarea", { id: "ldwq-notes", class: "field__input ldwq-instructions", rows: "3", maxlength: "1000",
      placeholder: "For example: which side should look best, a feature that must stay support-free, or how the part will be used." });
    notesBox.value = quote.notes || "";
    var notesStatus = el("span", { class: "ldwq-hint ldwq-notes-status", "aria-live": "polite" });
    var saveTimer;
    notesBox.addEventListener("input", function () {
      clearTimeout(saveTimer);
      cartChanged();
      notesStatus.textContent = "";
      saveTimer = setTimeout(function () {
        fetch(self.api + "/api/quotes/" + quote.id + "/notes", {
          method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ notes: notesBox.value }),
        })
          .then(function (r) { return r.json().then(function (b) { if (!r.ok) throw new Error(b.error); }); })
          .then(function () { notesStatus.textContent = "Saved with your quote."; })
          .catch(function (e) { notesStatus.textContent = (e && e.message) || "Couldn't save. Please try again."; });
      }, 800);
    });

    var order = quote.ordering ? addButton
      : self.contactUrl ? el("a", { class: "button", href: self.contactUrl, text: "Request this print" }) : null;
    var restart = el("button", { type: "button", class: "button button--secondary", text: quote.ordering ? "Quote another part" : "Quote another file" });
    var manual = self.manualQuote(quote, function () {
      var opt = current();
      return { opt: opt, qty: state.qty, color: state.color, notes: notesBox.value, maxQty: maxQty };
    });
    restart.addEventListener("click", function () { self.showUpload(); });

    var m = quote.model.dimensions_mm;
    this.render(el("div", { class: "ldwq-panel ldwq-quote" },
      el("p", { class: "ldwq-model", text: quote.filename + " · " + m[0] + " × " + m[1] + " × " + m[2] + " mm" }),
      progressLine,
      materialGroup.node, colorField, qualityGroup.node,
      el("div", { class: "ldwq-field" }, el("label", { for: "ldwq-qty", text: "Quantity (max " + maxQty + ")" }),
        el("div", { class: "ldwq-stepper" }, minus, qtyInput, plus)),
      priceArea,
      el("div", { class: "ldwq-field" },
        el("label", { for: "ldwq-notes", text: "Special instructions (optional)" }), notesBox, notesStatus),
      quote.ordering ? termsField : null,
      el("p", { class: "ldwq-ref", text: "Quote reference: " + (quote.reference || quote.id) }),
      el("div", { class: "ldwq-actions" }, order, restart), quote.ordering ? cartStatus : null, manual));
    flagPending();
    showProgress();
    updateAddButton();
    showColors(false);
    setQty(1);
  };

  // ---- manual quote: a request to the shop owner through the store's own contact form ----

  // A closed dropdown that opens a short form (name, email, quantity, message). The backend keeps the quote's
  // files and gives the owner's download link; the request goes out as a contact-form email.
  // The form is a real store contact form, so Shopify's own spam check (hCaptcha) protects it like the
  // contact page's; it posts into a hidden frame so the customer stays on the quote.
  // `picked()` -> {opt, qty, color, notes, maxQty}: what the customer has chosen on the quote.
  Widget.prototype.manualQuote = function (quote, picked) {
    var self = this;
    var name = el("input", { type: "text", id: "ldwq-m-name", class: "field__input", autocomplete: "name", maxlength: "100", required: "" });
    var email = el("input", { type: "email", id: "ldwq-m-email", class: "field__input", autocomplete: "email", maxlength: "200", required: "" });
    var qty = el("input", { type: "number", id: "ldwq-m-qty", class: "field__input ldwq-qty", min: "1", step: "1", inputmode: "numeric", required: "" });
    var message = el("textarea", { id: "ldwq-m-message", class: "field__input ldwq-instructions", rows: "3", maxlength: "2000",
      placeholder: "For example: a target price, a deadline, or anything we should know." });
    var send = el("button", { type: "submit", class: "button", text: "Send request" });
    var cancel = el("button", { type: "button", class: "button button--secondary", text: "Cancel" });
    var status = el("p", { class: "ldwq-hint ldwq-cart-status", "aria-live": "polite" });
    var frameName = "ldwq-contact-" + quote.id;
    var frame = el("iframe", { name: frameName, title: "Security check", class: "ldwq-manual-frame", hidden: "", tabindex: "-1" });
    var fields = el("div", { hidden: "" });   // the contact[...] fields, filled in on send
    function field(label, input) {
      return el("div", { class: "ldwq-field" }, el("label", { for: input.id, text: label }), input);
    }
    var form = el("form", { class: "ldwq-manual", novalidate: "", method: "post", action: shopRoot() + "contact",
                            "accept-charset": "UTF-8", target: frameName },
      el("input", { type: "hidden", name: "form_type", value: "contact" }), el("input", { type: "hidden", name: "utf8", value: "✓" }), fields,
      el("p", { class: "ldwq-hint", text: "We'll review your file (quote " + (quote.reference || quote.id) + ") and email you a price." }),
      field("Name", name), field("Email", email), field("Quantity wanted", qty), field("Message (optional)", message),
      el("div", { class: "ldwq-actions" }, send, cancel), status);
    var wrap = el("details", { class: "ldwq-manual-wrap" },
      el("summary", {}, el("span", {}, document.createTextNode("Request a manual quote"),
        el("small", { text: "Price seem high, or need more than " + picked().maxQty + "?" }))), form, frame);
    wrap.addEventListener("toggle", function () {
      if (!wrap.open || sent) return;
      qty.value = String(picked().qty);
      protect().catch(function () {});
      name.focus();
    });
    var sent = false, ready = false, bound = null;
    cancel.addEventListener("click", function () { wrap.open = false; });

    // Shopify's spam check on this form, when the store has it on (Online Store > Preferences).
    function protect() {
      var captcha = window.Shopify && Shopify.captcha;
      if (!captcha || !captcha.protect) return Promise.resolve();
      if (!bound) bound = new Promise(function (resolve, reject) {
        if (!form.dataset.cptcha) captcha.protect(form, resolve);   // not yet bound by a field getting focus
        var tries = 0, poll = setInterval(function () {
          if (form.hasAttribute("data-hcaptcha-bound")) { clearInterval(poll); resolve(); }
          else if (++tries > 150) { clearInterval(poll); bound = null; reject(new Error("We couldn't load the security check. Please try again.")); }
        }, 100);
      });
      return bound;
    }

    // Submits the filled-in form (through Shopify's check) into the frame and reads where the store sent it.
    function post() {
      return new Promise(function (resolve, reject) {
        var timer;
        function wait(ms) { clearTimeout(timer); timer = setTimeout(function () { done(new Error("We couldn't send your request. Please try again or contact us directly.")); }, ms); }
        function done(result) {
          clearTimeout(timer);
          frame.removeEventListener("load", loaded);
          frame.hidden = true;
          ready = false;
          if (result instanceof Error) reject(result); else resolve(result);
        }
        function loaded() {
          var url = "";
          try { url = frame.contentWindow.location.href; } catch (e) { /* not the store's page */ }
          if (/contact_posted=true/.test(url)) return done("sent");
          if (/\/challenge/.test(url)) {   // the store wants a visible check: show it right here
            frame.hidden = false;
            status.textContent = "Almost done: please finish the quick security check below.";
            return wait(600000);
          }
          done(new Error("We couldn't send your request. Please try again or contact us directly."));
        }
        frame.addEventListener("load", loaded);
        wait(600000);   // an hCaptcha puzzle, if one pops up, takes the customer a moment
        ready = true;
        if (form.requestSubmit) form.requestSubmit(); else form.dispatchEvent(new Event("submit", { cancelable: true }));
      });
    }

    form.addEventListener("submit", function (e) {
      if (ready) return;   // second pass: on to Shopify's check and the frame
      e.preventDefault();
      e.stopImmediatePropagation();
      var q = Number(qty.value);
      if (!name.value.trim()) return fail("Please enter your name.", name);
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim())) return fail("Please enter a valid email address.", email);
      if (!Number.isInteger(q) || q < 1 || q > 100000) return fail("Please enter how many you'd like.", qty);
      send.disabled = true;
      send.textContent = "Sending…";
      status.textContent = "";
      Promise.all([
        fetch(self.api + "/api/quotes/" + quote.id + "/manual", { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" })
          .then(function (r) { return r.json().then(function (b) { if (!r.ok) throw new Error(b.error); return b; }); }),
        protect(),
      ])
        .then(function (res) {
          fields.replaceChildren.apply(fields, contactFields(quote, picked(), res[0], name.value.trim(), email.value.trim(), q, message.value.trim())
            .map(function (f) { return el("input", { type: "hidden", name: f[0], value: f[1] }); }));
          return post();
        })
        .then(function () {
          sent = true;
          form.replaceChildren(el("p", { class: "ldwq-manual__title", text: "Request sent" }),
            el("p", { class: "ldwq-hint", text: "Thanks! We've received your request for quote " + (quote.reference || quote.id) +
              " and will email you at " + email.value.trim() + "." }));
        })
        .catch(function (err) {
          send.textContent = "Send request";
          send.disabled = false;
          status.textContent = err instanceof TypeError ? "We couldn't send your request. Please check your connection and try again."
            : (err && err.message) || "Something went wrong. Please try again.";
        });
    });
    function fail(text, input) {
      status.textContent = text;
      input.focus();
    }
    return wrap;
  };

  // The contact-form fields; each contact[...] field becomes a line in the owner's email, and the
  // customer's email is its reply-to address.
  function contactFields(quote, pick, link, name, email, qty, message) {
    var opt = pick.opt, m = quote.model && quote.model.dimensions_mm;
    var lines = ["Manual quote request for " + (quote.reference || quote.id)];
    lines.push("File: " + quote.filename + (m ? " (" + m[0] + " × " + m[1] + " × " + m[2] + " mm)" : ""));
    if (opt) {
      var shown = pick.qty + " × " + opt.material_label + ", " + opt.quality_label + (pick.color ? ", " + pick.color : "");
      lines.push("Selected on the page: " + shown + ", online price " + money(priceFor(opt, pick.qty).total));
      if (qty !== pick.qty && qty <= pick.maxQty) lines.push("Online price for " + qty + ": " + money(priceFor(opt, qty).total));
      if (qty > pick.maxQty) lines.push("Quantity wanted is over the online maximum of " + pick.maxQty + ".");
    }
    if (pick.notes.trim()) lines.push("Special instructions: " + pick.notes.trim());
    lines.push("", "Message: " + (message || "(none)"));
    return [["contact[Name]", name], ["contact[email]", email],
      ["contact[Quote reference]", link.reference], ["contact[Quantity wanted]", String(qty)],
      ["contact[Model file]", link.file_url], ["contact[Comment]", lines.join("\n")]];
  }

  // ---- the store's cart ----

  function shopRoot() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
  }

  // Adds the backend's line to the store cart and refreshes the header's cart count. A product
  // made a moment ago can take a second to become buyable, so a refusal is retried a few times.
  function addToShopCart(line, attempt) {
    attempt = attempt || 1;
    return fetch(shopRoot() + "cart/add.js", {
      method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [{ id: line.variant_id, quantity: line.quantity, properties: line.properties }],
                             sections: "cart-icon-bubble" }),
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (b) {
        if (!r.ok) {
          if (attempt < 4 && (r.status === 404 || r.status === 422)) {
            return new Promise(function (res) { setTimeout(res, 1500 * attempt); })
              .then(function () { return addToShopCart(line, attempt + 1); });
          }
          throw new Error("We couldn't add this to your cart. Please try again or contact us.");
        }
        var bubble = document.getElementById("cart-icon-bubble");
        var html = b.sections && b.sections["cart-icon-bubble"];
        if (bubble && html) {
          var section = new DOMParser().parseFromString(html, "text/html").querySelector(".shopify-section");
          if (section) bubble.innerHTML = section.innerHTML;
        }
        return b;
      });
    });
  }

  function mount() {
    document.querySelectorAll("[data-ldw-quote]").forEach(function (root) {
      if (!root.dataset.ldwMounted) { root.dataset.ldwMounted = "1"; new Widget(root); }
    });
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", mount);
  else mount();
  document.addEventListener("shopify:section:load", mount);  // theme editor live preview
})();
